import { NmLoader } from './NmLoader';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { library as libraryApi, type LibraryItem } from '../services/api';

/** Payload for creating a library exercise from an existing workout. */
export interface AddToLibraryWorkoutData {
  title?: string;
  description?: string | null;
  workout_type_id?: number;
  structure?: {
    structure: unknown[];
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  };
  tss_planned?: number | null;
  if_planned?: number | null;
  total_time_planned?: number | null;
  distance_planned?: number | null;
  calories_planned?: number | null;
}

interface AddToLibraryModalProps {
  workoutData: AddToLibraryWorkoutData;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Lets the user pick an existing library folder (or create one) and save a workout template.
 */
export function AddToLibraryModal({ workoutData, onClose, onSuccess }: AddToLibraryModalProps) {
  const { t } = useTranslation();
  const [libraries, setLibraries] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [showNewInput, setShowNewInput] = useState(false);
  const [newLibName, setNewLibName] = useState('');
  const [creatingLib, setCreatingLib] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchLibraries = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await libraryApi.getAll();
      setLibraries(res.data?.data ?? []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('library.addToLibrary.error.loadFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLibraries();
  }, [fetchLibraries]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    if (showNewInput) inputRef.current?.focus();
  }, [showNewInput]);

  const handleCreateLibrary = async () => {
    const name = newLibName.trim();
    if (!name) return;
    try {
      setCreatingLib(true);
      setError('');
      await libraryApi.create(name);
      const res = await libraryApi.getAll();
      const all = res.data?.data ?? [];
      setLibraries(all);
      const created = all.find(l => l.lib_name === name);
      if (created) setSelectedId(created.id);
      setShowNewInput(false);
      setNewLibName('');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('library.addToLibrary.error.createFailed');
      setError(msg);
    } finally {
      setCreatingLib(false);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      setError('');
      await libraryApi.addExercise(selectedId, workoutData);
      onSuccess?.();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('library.addToLibrary.error.saveFailed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div className="modal-surface" style={s.card} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <span style={s.title}>{t('library.addToLibrary.title')}</span>
          <span style={s.sub}>
            {workoutData.title ? `"${workoutData.title}"` : t('library.addToLibrary.saveTemplate')}
          </span>
        </div>

        <div style={s.body}>
          {loading ? (
            <div style={s.center}>
              <NmLoader size="md" label={t('library.addToLibrary.loading')} />
            </div>
          ) : (
            <>
              {libraries.length === 0 && !showNewInput && (
                <p style={s.empty}>{t('library.addToLibrary.empty')}</p>
              )}

              <div style={s.list}>
                {libraries.map(lib => {
                  const selected = lib.id === selectedId;
                  return (
                    <button
                      key={lib.id}
                      type="button"
                      style={{ ...s.libRow, ...(selected ? s.libRowSelected : {}) }}
                      onClick={() => setSelectedId(selected ? null : lib.id)}
                    >
                      <span style={{ ...s.radio, ...(selected ? s.radioOn : {}) }}>
                        {selected && <span style={s.radioDot} />}
                      </span>
                      <span style={s.libMeta}>
                        <span style={s.libName}>{lib.lib_name}</span>
                        <span style={s.libCount}>{t('library.addToLibrary.workoutCount', { count: lib.exercises.length, plural: lib.exercises.length !== 1 ? 's' : '' })}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {showNewInput ? (
                <div style={s.newRow}>
                  <input
                    ref={inputRef}
                    style={s.input}
                    placeholder={t('library.addToLibrary.newNamePlaceholder')}
                    value={newLibName}
                    onChange={e => setNewLibName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') void handleCreateLibrary(); }}
                  />
                  <button
                    type="button"
                    style={{ ...s.iconBtn, opacity: newLibName.trim() ? 1 : 0.4 }}
                    disabled={!newLibName.trim() || creatingLib}
                    onClick={() => void handleCreateLibrary()}
                    title={t('library.addToLibrary.createLibrary')}
                  >
                    {creatingLib ? '…' : '✓'}
                  </button>
                  <button
                    type="button"
                    style={s.iconBtn}
                    onClick={() => { setShowNewInput(false); setNewLibName(''); }}
                    title={t('common.cancel')}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <button type="button" style={s.addNewBtn} onClick={() => setShowNewInput(true)}>
                  {t('library.addToLibrary.createNew')}
                </button>
              )}
            </>
          )}

          {error && <p style={s.error}>{error}</p>}
        </div>

        <div style={s.footer}>
          <button type="button" style={s.ghostBtn} onClick={onClose}>{t('common.cancel')}</button>
          <button
            type="button"
            style={{ ...s.primaryBtn, opacity: selectedId && !saving ? 1 : 0.45 }}
            disabled={!selectedId || saving}
            onClick={() => void handleSave()}
          >
            {saving ? t('common.saving') : t('library.addToLibrary.add')}
          </button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(14,18,28,0.55)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1700,
  },
  card: {
    width: 420,
    maxWidth: 'calc(100vw - 32px)',
    background: 'var(--surface-0)',
    borderRadius: 18,
    boxShadow: 'var(--nm-raised-lg)',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 20px 12px',
    borderBottom: '1px solid rgba(148,163,188,0.15)',
  },
  title: {
    display: 'block',
    fontFamily: 'var(--font-display)',
    fontSize: 15,
    fontWeight: 800,
    color: 'var(--fg-1)',
  },
  sub: {
    display: 'block',
    fontSize: 12,
    color: 'var(--fg-4)',
    marginTop: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    padding: '14px 20px 8px',
    maxHeight: 340,
    overflowY: 'auto',
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '24px 0',
  },
  muted: { fontSize: 12, color: 'var(--fg-3)' },
  empty: {
    fontSize: 12,
    color: 'var(--fg-4)',
    textAlign: 'center',
    margin: '0 0 10px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  libRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '11px 12px',
    borderRadius: 12,
    border: '1.5px solid transparent',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised-sm)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    width: '100%',
    transition: 'border-color 140ms',
  },
  libRowSelected: {
    borderColor: 'color-mix(in srgb, var(--text-accent) 35%, transparent)',
    background: 'color-mix(in srgb, var(--text-accent) 4%, transparent)',
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '2px solid var(--fg-4)',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
  },
  radioOn: {
    borderColor: 'var(--text-accent)',
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'var(--text-accent)',
  },
  libMeta: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  libName: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--fg-1)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  libCount: {
    fontSize: 11,
    color: 'var(--fg-4)',
  },
  addNewBtn: {
    marginTop: 10,
    padding: '8px 4px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-accent)',
  },
  newRow: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  input: {
    flex: 1,
    height: 38,
    borderRadius: 10,
    border: 'none',
    outline: 'none',
    background: 'var(--surface-sunken)',
    boxShadow: 'var(--nm-inset-sm)',
    padding: '0 12px',
    fontFamily: 'var(--font-display)',
    fontSize: 13,
    color: 'var(--fg-1)',
  },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--surface-sunken)',
    color: 'var(--fg-2)',
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
  },
  error: {
    margin: '10px 0 0',
    fontSize: 12,
    color: 'var(--color-danger)',
  },
  footer: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    padding: '12px 20px 16px',
    borderTop: '1px solid rgba(148,163,188,0.15)',
  },
  ghostBtn: {
    height: 36,
    padding: '0 16px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-display)',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--fg-3)',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised-sm)',
  },
  primaryBtn: {
    height: 36,
    padding: '0 18px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-display)',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(135deg,color-mix(in srgb, var(--text-accent) 80%, transparent),var(--text-accent))',
    boxShadow: '0 4px 14px color-mix(in srgb, var(--text-accent) 25%, transparent)',
  },
};
